/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import qs from "query-string";
import { Trans } from "react-i18next";
import { useWindowSize } from "react-use";
import { Navigate, useNavigate, useLocation } from "react-router-dom";

import { useMutation, useQuery } from "@tanstack/react-query";

import {
  GridCell,
  GridVideoView,
  useRoomConnection,
  VideoGrid,
  // eslint-disable-next-line import/no-unresolved
} from "@whereby.com/browser-sdk/react";

import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import Title from "../../components/Title";
import Curve from "../../components/Animations/PageTransitions/Curve";
import BackButton from "../../components/BackButton";

import Microphone from "../../assets/icons/Microphone.svg";
import MicrophoneOff from "../../assets/icons/MicrophoneOff.svg";

import {
  MUTATION_CREATE_VIDEO_ROOM,
  MUTATION_DELETE_VIDEO_ROOM,
} from "../../config/graphql/mutation";
import { client } from "../../bit/board/hooks/useGraphQLClient";
import { useCheckinAccessNavigation } from "../../lib/hooks/useCheckinAccessNavigation";
import { useDebounceStatus } from "../../lib/hooks/useDebounceStatus";
import { useTimeoutFunction } from "../../lib/hooks/useTimeoutFunction";
import Modal from "../../components/Modal";

const MuiGridCell = styled(GridCell)(({ theme }) => ({
  borderRadius: "1rem",
  border: `0.5rem solid ${theme.palette.primary.main}`,
  background: "black",
}));

const Controls = styled("div")({
  display: "flex",
  transform: "translateY(-100%)",
});

const ControlsButton = styled("div")(({ theme }) => ({
  display: "flex",
  padding: "2rem",
  margin: "1rem",
  border: "2px solid white",
  borderRadius: "1rem",
  background: "rgba(35, 35, 35, 0.725)",

  h1: {
    color: "white",
    marginRight: "1rem",
  },
}));

const Room = React.memo(
  ({
    roomUrl,
    handleAccessNavigation,
  }: {
    roomUrl: string;
    handleAccessNavigation: () => void;
  }) => {
    const navigate = useNavigate();

    const { width, height } = useWindowSize();

    const [isMicActive, setIsMicActive] = useState<boolean>(true);

    const orientation = width < height ? "portrait" : "landscape";

    const { state, actions } = useRoomConnection(roomUrl, {
      localMediaOptions: {
        audio: true,
        video: true,
      },
    });

    const { remoteParticipants, connectionStatus } = state;

    const { joinRoom, leaveRoom, toggleCamera, toggleMicrophone } = actions;

    useEffect(() => {
      joinRoom();

      return () => {
        leaveRoom();
      };
    }, [joinRoom, leaveRoom]);

    useEffect(() => {
      if (connectionStatus === "left") {
        handleAccessNavigation();
      }
    }, [connectionStatus]);

    return (
      <div
        className={`${
          remoteParticipants.length === 0 && orientation === "landscape"
            ? "w-25"
            : "w-50"
        } ${
          remoteParticipants.length === 0 && orientation !== "landscape"
            ? "h-50"
            : "h-100"
        } p-5`}
      >
        <VideoGrid
          videoGridGap={10}
          renderParticipant={({ participant }) => {
            return (
              <MuiGridCell
                participant={participant}
                className="video-grid-cell"
              >
                <GridVideoView muted={participant.isLocalClient} />
                {connectionStatus === "connected" &&
                  participant.isLocalClient && (
                    <Controls className="d-flex justify-content-between p-3">
                      <ControlsButton
                        onClick={() => {
                          leaveRoom();
                        }}
                      >
                        <Typography variant="h3" className="text-white">
                          <Trans i18nKey="videoCall:endCall" />
                        </Typography>
                        {/* <EndCall width="3.5rem" fill="white" /> */}
                      </ControlsButton>
                      <ControlsButton
                        onClick={() => {
                          setIsMicActive((prev) => !prev);
                          toggleMicrophone();
                        }}
                      >
                        {isMicActive ? (
                          <Microphone width="3.5rem" fill="white" />
                        ) : (
                          <MicrophoneOff width="3.5rem" fill="white" />
                        )}
                      </ControlsButton>
                    </Controls>
                  )}
              </MuiGridCell>
            );
          }}
        />
      </div>
    );
  },
);

const VideoCall = () => {
  const navigate = useNavigate();

  const { search, state } = useLocation();
  const query = qs.parse(search);

  const eventId = query.id as string;
  const type = query.type as string;

  const preCreatedRoom = (state as { videoRoom?: IVideoRoom } | null)
    ?.videoRoom;

  const { handleCheckinAccessNavigation, accessSwitchModal } =
    useCheckinAccessNavigation({
      type,
    });

  const { data, status: requestStatus } = useQuery(
    [`access/event/${eventId}/video`],
    () => {
      return client.request(MUTATION_CREATE_VIDEO_ROOM, {
        id: eventId,
      });
    },
    {
      enabled: !!eventId && !preCreatedRoom,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const roomId = preCreatedRoom?.id ?? data?.accessEventCreateVideoRoom?.id;
  const roomUrl =
    preCreatedRoom?.roomUrl ?? data?.accessEventCreateVideoRoom?.roomUrl;

  const roomStatus = preCreatedRoom ? "success" : requestStatus;

  const { mutate: onLeaveRoom } = useMutation(async (id: string) => {
    return client.request(MUTATION_DELETE_VIDEO_ROOM, { id });
  });

  useEffect(() => {
    return () => {
      if (roomId) {
        onLeaveRoom(roomId);
      }
    };
  }, [roomId, onLeaveRoom]);

  const onDismiss = useCallback(() => {
    if (roomStatus === "error") {
      navigate(-1);
    }
  }, [roomStatus, navigate]);

  const status = useMemo(() => {
    if (roomStatus === "error") {
      return "error";
    }
    if (roomStatus === "success") {
      return "success";
    }
    return roomStatus;
  }, [roomStatus]);

  const [showModal, setShowModal] = useState(!preCreatedRoom);

  const timeout = useMemo(() => {
    if (status === "error") {
      return 5000;
    }

    if (status === "success") {
      return 4000;
    }
    return 0;
  }, [status]);

  useTimeoutFunction(() => {
    if (status === "error") {
      onDismiss();
    } else if (status === "success") {
      setShowModal(false);
    }
  }, timeout);

  if (!eventId) {
    return <Navigate to="/" replace />;
  }

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column h-100">
        <BackButton to="/" />
        <Title>
          <Typography variant="h1">
            <Trans i18nKey="videoCall:title" />
          </Typography>
        </Title>

        {showModal &&
          (roomStatus === "loading" ||
            roomStatus === "error" ||
            status === "success") && (
            <Modal
              title={<Trans i18nKey={`videoCall:status.${status}.title`} />}
              message={<Trans i18nKey={`videoCall:status.${status}.message`} />}
              status={status}
              onDismiss={onDismiss}
            />
          )}

        {roomUrl && (
          <Room
            roomUrl={roomUrl}
            handleAccessNavigation={handleCheckinAccessNavigation}
          />
        )}
        {accessSwitchModal}
      </div>
    </Curve>
  );
};

export default VideoCall;
